import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Settings as SettingsIcon, Save, Building2, CreditCard, FileText, ImageIcon, Trash2, Plus, Check } from "lucide-react";
import { useSelector } from "react-redux";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { API_BASE_URL } from "../../../App";
import { COUNTRY_OPTIONS, INDIA_STATES_AND_UT, isIndiaCountry } from "../../../constants/countryStates";

interface AppSettings {
  quotationSeries: string;
  numberSeries: number;
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyCountry: string;
  companyState: string;
  companyGst: string;
  companyLogo: string;
  bankName: string;
  bankAccountHolder: string;
  bankAccountNo: string;
  bankIfsc: string;
  bankBranch: string;
}

interface QuotationSeriesItem {
  id: number;
  name: string;
  isDefault: boolean;
}

const defaultSettings: AppSettings = {
  quotationSeries: "QT",
  numberSeries: 1,
  companyName: "",
  companyAddress: "",
  companyPhone: "",
  companyEmail: "",
  companyCountry: "India",
  companyState: "",
  companyGst: "",
  companyLogo: "",
  bankName: "",
  bankAccountHolder: "",
  bankAccountNo: "",
  bankIfsc: "",
  bankBranch: "",
};

export const SettingsPage = () => {
  const { authToken = "", id = "" } = useSelector((state: any) => state.loginData || {});
  const [form, setForm] = useState<AppSettings>(defaultSettings);
  const [seriesList, setSeriesList] = useState<QuotationSeriesItem[]>([]);
  const [newSeriesName, setNewSeriesName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seriesBusy, setSeriesBusy] = useState(false);

  const handleCompanyLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG or JPEG).");
      return;
    }
    if (file.size > 450_000) {
      toast.error("Logo must be under 450 KB so it can be stored and embedded in PDFs reliably.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result;
      if (typeof data === "string") {
        setForm((p) => ({ ...p, companyLogo: data }));
        toast.success("Logo loaded — click Save All Settings to store it.");
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const fetchSeriesList = useCallback(async () => {
    if (!id || !authToken) return;
    try {
      const response = await axios.get(`${API_BASE_URL}account/${id}/quotation-series`, {
        params: { firstResults: 0, maxResults: 1000 },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const items: QuotationSeriesItem[] = response.data.series || [];
      setSeriesList(items);
      const active = items.find((item) => item.isDefault) || items[0];
      if (active?.name) {
        setForm((prev) => ({ ...prev, quotationSeries: active.name }));
      }
    } catch {
      toast.error("Failed to load quotation series");
    }
  }, [authToken, id]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!id) { setLoading(false); return; }
      try {
        const [settingsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}account/${id}/settings`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          fetchSeriesList(),
        ]);
        const d = settingsResponse.data;
        setForm({
          quotationSeries: d.quotationSeries || "QT",
          numberSeries: Number(d.numberSeries || 1),
          companyName: d.companyName || "",
          companyAddress: d.companyAddress || "",
          companyPhone: d.companyPhone || "",
          companyEmail: d.companyEmail || "",
          companyCountry: d.companyCountry || "India",
          companyState: d.companyState || "",
          companyGst: d.companyGst || "",
          companyLogo: d.companyLogo || "",
          bankName: d.bankName || "",
          bankAccountHolder: d.bankAccountHolder || "",
          bankAccountNo: d.bankAccountNo || "",
          bankIfsc: d.bankIfsc || "",
          bankBranch: d.bankBranch || "",
        });
      } catch {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [authToken, id, fetchSeriesList]);

  const preview = useMemo(() => {
    const series = (form.quotationSeries || "QT").trim() || "QT";
    const number = Number(form.numberSeries || 1);
    return `${series}-${String(number).padStart(3, "0")}-R0`;
  }, [form.numberSeries, form.quotationSeries]);

  const set = (field: keyof AppSettings) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAddSeries = async () => {
    const name = newSeriesName.trim().toUpperCase();
    if (!name) {
      toast.error("Enter a quotation series name");
      return;
    }
    setSeriesBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}account/${id}/quotation-series/create`,
        { name, isDefault: seriesList.length === 0 },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setNewSeriesName("");
      toast.success("Quotation series added");
      await fetchSeriesList();
    } catch (error) {
      const message = axios.isAxiosError(error) && error.response?.data?.message
        ? String(error.response.data.message)
        : "Unable to add quotation series";
      toast.error(message);
    } finally {
      setSeriesBusy(false);
    }
  };

  const handleSetDefaultSeries = async (item: QuotationSeriesItem) => {
    if (item.isDefault) return;
    setSeriesBusy(true);
    try {
      await axios.put(
        `${API_BASE_URL}account/${id}/quotation-series/${item.id}/set-default`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setForm((prev) => ({ ...prev, quotationSeries: item.name }));
      toast.success(`Active series set to ${item.name}`);
      await fetchSeriesList();
    } catch (error) {
      const message = axios.isAxiosError(error) && error.response?.data?.message
        ? String(error.response.data.message)
        : "Unable to set active series";
      toast.error(message);
    } finally {
      setSeriesBusy(false);
    }
  };

  const handleDeleteSeries = async (item: QuotationSeriesItem) => {
    if (seriesList.length <= 1) {
      toast.error("At least one quotation series is required");
      return;
    }
    if (!window.confirm(`Delete quotation series "${item.name}"?`)) return;
    setSeriesBusy(true);
    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/quotation-series/${item.id}/delete`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      toast.success("Quotation series deleted");
      await fetchSeriesList();
    } catch (error) {
      const message = axios.isAxiosError(error) && error.response?.data?.message
        ? String(error.response.data.message)
        : "Unable to delete quotation series";
      toast.error(message);
    } finally {
      setSeriesBusy(false);
    }
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}account/${id}/settings`,
        { ...form, quotationSeries: form.quotationSeries.trim(), numberSeries: Number(form.numberSeries) },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Settings saved successfully");
      await fetchSeriesList();
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-500 p-8">Loading settings...</div>;

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <SettingsIcon className="text-[var(--sidebar-primary)]" size={30} />
          Settings
        </h1>
        <p className="text-gray-500 text-sm mt-1">Configure quotation numbering, company details, and bank information.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* ── Quotation Series ── */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={18} className="text-[var(--sidebar-primary)]" />
            <h2 className="text-lg font-semibold text-gray-800">Quotation Numbering</h2>
          </div>

          <div className="space-y-3">
            <Label>Quotation Series List</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={newSeriesName}
                onChange={(e) => setNewSeriesName(e.target.value.toUpperCase())}
                placeholder="e.g. Q-CTS-26/27"
                maxLength={50}
                className="sm:flex-1"
                disabled={seriesBusy}
              />
              <Button
                type="button"
                onClick={handleAddSeries}
                disabled={seriesBusy || !newSeriesName.trim()}
                className="gap-1.5"
              >
                <Plus size={16} />
                Add Series
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Add series prefixes here. The active series is used for new quotations and appears in the quotation filter.
            </p>

            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
              {seriesList.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-500 text-center">No series yet. Add one above.</p>
              ) : (
                seriesList.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between gap-3 px-4 py-3 ${
                      item.isDefault ? "bg-[var(--sidebar-light)]" : "bg-white"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{item.name}</p>
                      {item.isDefault && (
                        <p className="text-xs text-[var(--sidebar-primary)] font-medium">Active for new quotations</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {item.isDefault ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--sidebar-primary)]/10 px-2.5 py-1 text-xs font-semibold text-[var(--sidebar-primary)]">
                          <Check size={12} />
                          Active
                        </span>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={seriesBusy}
                          onClick={() => handleSetDefaultSeries(item)}
                        >
                          Set Active
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={seriesBusy || seriesList.length <= 1}
                        onClick={() => handleDeleteSeries(item)}
                        className="text-red-600 hover:text-red-700"
                        aria-label={`Delete ${item.name}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>Active Series</Label>
              <Input value={form.quotationSeries} readOnly className="bg-gray-50 font-semibold" />
              <p className="text-xs text-gray-500">Prefix used for every new quotation number.</p>
            </div>
            <div className="space-y-2">
              <Label>Number Series (next number)</Label>
              <Input
                type="number"
                min={1}
                value={form.numberSeries}
                onChange={(e) => setForm((p) => ({ ...p, numberSeries: Math.max(1, Number(e.target.value || 1)) }))}
                placeholder="1"
              />
              <p className="text-xs text-gray-500">Next number to use when a new quotation is created.</p>
            </div>
          </div>
          <div className="rounded-xl border border-dashed border-[var(--accent-primary)]/50 bg-[var(--sidebar-light)] p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Next Quotation Preview</p>
            <p className="text-2xl font-bold text-[var(--sidebar-primary)] mt-1">{preview}</p>
          </div>
        </div>

        {/* ── Company Details ── */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={18} className="text-[var(--sidebar-primary)]" />
            <h2 className="text-lg font-semibold text-gray-800">Company Details</h2>
          </div>
          <p className="text-xs text-gray-500 -mt-3">These details appear in the top-right corner of every quotation PDF.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input value={form.companyName} onChange={set("companyName")} placeholder="Cogent Tech Solutions" />
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <select
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sidebar-primary)]/30"
                value={form.companyCountry}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((p) => ({
                    ...p,
                    companyCountry: v,
                    companyState: isIndiaCountry(v) ? p.companyState : "",
                  }));
                }}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Address</Label>
              <Input value={form.companyAddress} onChange={set("companyAddress")} placeholder="258/1A, 200 Feet Service Road, Chennai - 600095" />
            </div>
            <div className="space-y-2">
              <Label>State / Province</Label>
              {isIndiaCountry(form.companyCountry) ? (
                <select
                  className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sidebar-primary)]/30"
                  value={form.companyState}
                  onChange={(e) => setForm((p) => ({ ...p, companyState: e.target.value }))}
                >
                  <option value="">Select state / UT</option>
                  {INDIA_STATES_AND_UT.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  value={form.companyState}
                  onChange={(e) => setForm((p) => ({ ...p, companyState: e.target.value }))}
                  placeholder="State or region"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>{isIndiaCountry(form.companyCountry) ? "GSTIN" : "Tax / VAT ID"}</Label>
              <Input
                value={form.companyGst}
                onChange={set("companyGst")}
                placeholder={isIndiaCountry(form.companyCountry) ? "33AXWPG7852P1ZI" : "Optional tax identifier"}
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.companyPhone} onChange={set("companyPhone")} placeholder="+91 98765 43210" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.companyEmail} onChange={set("companyEmail")} placeholder="info@cogent.com" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="flex items-center gap-2">
                <ImageIcon size={14} className="text-[var(--sidebar-primary)]" />
                Quotation PDF logo
              </Label>
              <p className="text-xs text-gray-500">
                Upload a square or wide logo (PNG/JPEG). It is saved with your settings and used on every quotation PDF. Maximum size 450 KB.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="hidden"
                    onChange={handleCompanyLogoChange}
                  />
                  <span className="cursor-pointer rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition">
                    Choose image…
                  </span>
                </label>
                {form.companyLogo ? (
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, companyLogo: "" }))}
                    className="inline-flex items-center gap-1 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 transition"
                  >
                    <Trash2 size={14} />
                    Remove logo
                  </button>
                ) : null}
              </div>
              {form.companyLogo ? (
                <div className="mt-2 flex items-center gap-4 rounded-xl border border-gray-100 bg-gray-50/80 p-4">
                  <img src={form.companyLogo} alt="Logo preview" className="h-14 w-auto max-w-[200px] object-contain" />
                  <span className="text-xs text-gray-500">Preview — appears top-left on quotations</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* ── Bank Details ── */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard size={18} className="text-[var(--sidebar-primary)]" />
            <h2 className="text-lg font-semibold text-gray-800">Bank Details</h2>
          </div>
          <p className="text-xs text-gray-500 -mt-3">These details appear in the payment summary section of every quotation PDF.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>Account Holder Name</Label>
              <Input value={form.bankAccountHolder} onChange={set("bankAccountHolder")} placeholder="Cogent Tech Solutions" />
            </div>
            <div className="space-y-2">
              <Label>Bank Name</Label>
              <Input value={form.bankName} onChange={set("bankName")} placeholder="Indian Overseas Bank" />
            </div>
            <div className="space-y-2">
              <Label>Account Number</Label>
              <Input value={form.bankAccountNo} onChange={set("bankAccountNo")} placeholder="069833000000113" />
            </div>
            <div className="space-y-2">
              <Label>IFSC Code</Label>
              <Input value={form.bankIfsc} onChange={set("bankIfsc")} placeholder="IOBA0000698" />
            </div>
            <div className="space-y-2">
              <Label>Branch</Label>
              <Input value={form.bankBranch} onChange={set("bankBranch")} placeholder="Teynampet Branch" />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" className="btn-primary flex items-center gap-2 px-8 py-3 rounded-xl" disabled={saving}>
            <Save size={16} />
            {saving ? "Saving..." : "Save All Settings"}
          </Button>
        </div>
      </form>
    </div>
  );
};
